import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/api/client'

/**
 * The Buchhaltung endpoints (CHG-2026-10-accounting-close-periods, D1).
 *
 * One request answers one close-bounded period for both Ansichten — the Übersicht sections, one row
 * per Tagesabschluss with Clemens' columns, and the Kontrolle — so switching the Ansicht never asks
 * again. The CSV and PDF exports are the same computation rendered server-side, which is why a
 * figure on the page cannot disagree with the document the controller files.
 */
export interface ClosingReportPeriod {
  /** `YYYY-MM` in Monat, else null. */
  month: string | null
  /** The previous close, inclusive; null "seit Inbetriebnahme" and for a period without a close. */
  from: string | null
  /** The period's last close; null when it has none. */
  to: string | null
  closeCount: number
  timezone: string
  restaurantId: number
  restaurantName: string | null
  /** TAGESABSCHLUSS / MONATSBERICHT / ZEITRAUMBERICHT, as the exports print it. */
  headline: string
  periodLabel: string
}

export interface ClosingRateRow {
  vatCode: string
  netCents: number
  vatCents: number
  grossCents: number
}

export interface ClosingSlice {
  key: string
  label: string | null
  grossCents: number
}

export interface ClosingPaymentRow {
  method: string
  /** The goods this method paid for — the one figure every section shows for it. */
  grossCents: number
  grossByRate: Record<string, number>
}

/**
 * The payment side of the page, closed against the revenue side, in two groups (D10): an der Kassa
 * and online. `totalCents` equals `umsatz.grossCents` by construction; `unassignedCents` is
 * whatever separates the two sides, named instead of left for the reader to subtract.
 */
export interface ClosingZahlungen {
  kassa: Array<ClosingPaymentRow>
  kassaCents: number
  online: Array<ClosingPaymentRow>
  onlineCents: number
  unassignedCents: number
  totalCents: number
}

export interface ClosingReasonRow {
  reasonCode: string
  reasonLabel: string | null
  warenwertCents: number
  /** The value was reconstructed at resync time from a current price, not captured at booking. */
  approximated: boolean
  valueMissing: boolean
}

/** One row of the controller's sheet (D4, D11); revenue maps are keyed by VAT code. */
export interface ClosingClemens {
  umsatz: number
  /** Mitarbeiter: staff, operator-less and the counter part of online orders. */
  staff: Record<string, number>
  selfCheckout: Record<string, number>
  /** The remotely paid part of online orders. */
  online: Record<string, number>
  unbar: number
  bons: number
  bankomat: number
  barumsatz: number
  trinkgeld: number
}

export interface ClosingCloseRow {
  /** The cloud id of the close's latest Beleg — what selects it. */
  ref: string
  closedAt: string
  /** The previous close; null for the Standort's first. */
  from: string | null
  /** Calendar days from the previous close's day to this one's; more than one is marked. */
  daySpan: number
  /** Its window starts in the month before the one it was closed in. */
  crossesMonth: boolean
  belegCount: number
  /** What its Belege printed. */
  recorded: {
    cashCents: number
    cardCents: number
    voucherCents: number
    totalCents: number
  }
  /** Online revenue paid in its window. */
  onlineCents: number
  clemens: ClosingClemens
}

/** The recorded Belege against the same figure recomputed from the synced data (D5). */
export interface ClosingKontrolle {
  belegCount: number
  recordedCents: number
  recomputedCents: number
  /** Money vouchers took above the goods — no revenue. */
  voucherSurplusCents: number
  /** Online-order payments the Belege claimed at the counter. */
  claimedOnlineCents: number
  /** POS money on no Beleg (no operator, or no Beleg of that operator) — less than on the Belege. */
  outsideBelegeCents: number
  differences: Array<{ ref: string; closedAt: string; differenceCents: number }>
}

/** What was paid after the latest close (D6) — never in a total, a row or a file. */
export interface ClosingOpen {
  /** The latest close; null at a Standort that never closed. */
  sinceClose: string | null
  /** The first payment of the open amount; null when there is none. */
  since: string | null
  grossCents: number
}

export interface ClosingPeriodReport {
  mode: 'TAG' | 'MONAT' | 'ZEITRAUM'
  period: ClosingReportPeriod
  /** The sections — absent for a period without a close (no calendar fallback). */
  umsatz?: {
    grossCents: number
    vorOrtCents: number
    onlineCents: number
    rates: Array<ClosingRateRow>
  }
  sparten?: Array<ClosingSlice>
  kanaele?: Array<ClosingSlice>
  zahlungen?: ClosingZahlungen
  trinkgeld?: {
    byMethod: Array<ClosingSlice>
    totalCents: number
    recorded: boolean
  }
  entnahmen?: {
    reasons: Array<ClosingReasonRow>
    warenwertCents: number
    approximated: boolean
    valueMissing: boolean
  }
  closes: Array<ClosingCloseRow>
  /** The VAT codes the Clemens table has columns for: 20 / 10 / 4,9 % and any other with revenue. */
  clemensRates: Array<string>
  clemensSum: ClosingClemens
  kontrolle: ClosingKontrolle
  open: ClosingOpen | null
  lastSync: string | null
}

/** A period whose sections are present — every period with at least one close. */
export type ClosingSheet = ClosingPeriodReport &
  Required<
    Pick<
      ClosingPeriodReport,
      'umsatz' | 'sparten' | 'kanaele' | 'zahlungen' | 'trinkgeld' | 'entnahmen'
    >
  >

export function hasSheet(report: ClosingPeriodReport): report is ClosingSheet {
  return report.umsatz != null
}

/** One close of a month, for the Tag and Zeitraum pickers — no figures. */
export interface ClosingCloseOption {
  ref: string
  closedAt: string
  from: string | null
  daySpan: number
  crossesMonth: boolean
}

/**
 * A filing report is a snapshot the reader refreshes deliberately — the same reasoning as the other
 * report queries, and more so here: these figures end up in a UVA.
 */
const REPORT_STALE_TIME = 60_000

/**
 * `params` is the query string, so it is also the cache key: two selections that ask the same
 * question share one cached answer. Null means the selection is not complete enough to ask — no
 * Standort yet, or a Tag or Zeitraum still being picked — and no request goes out. The previous
 * period stays as placeholder meanwhile, so the pickers keep opening where the reader was.
 */
export function useClosingPeriodQuery(params: string | null) {
  return useQuery({
    queryKey: ['closing-period', params],
    queryFn: () =>
      apiRequest<ClosingPeriodReport>(
        `/api/v1/admin/reports/closing/period?${params}`,
      ),
    staleTime: REPORT_STALE_TIME,
    placeholderData: keepPreviousData,
    enabled: params != null,
  })
}

/** The Standort's closes of `month`, newest first; null asks nothing. */
export function useClosesQuery(restaurantId: number | null, month: string) {
  const params =
    restaurantId == null
      ? null
      : new URLSearchParams({
          restaurantId: String(restaurantId),
          month,
        }).toString()
  return useQuery({
    queryKey: ['closing-closes', params],
    queryFn: () =>
      apiRequest<{ month: string; closes: Array<ClosingCloseOption> }>(
        `/api/v1/admin/reports/closing/closes?${params}`,
      ),
    staleTime: REPORT_STALE_TIME,
    enabled: params != null,
  })
}
